import { useState, useEffect, useCallback } from "react";
import { RefreshCw, Monitor, Radio } from "lucide-react";
import axios, { API } from "@/utils/api";
import { useT, useDeptName } from "@/contexts/LanguageContext";
import { LIVE_CHART_COLORS } from "@/utils/constants";
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend
} from "recharts";

const LiveMonitorPanel = ({ clientId, assetUuid }) => {
  const t = useT();
  const dn = useDeptName();
  const [timeseries, setTimeseries] = useState(null);
  const [selectedDept, setSelectedDept] = useState("");
  const [polling, setPolling] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [minutes, setMinutes] = useState(60);

  const fetchTimeseries = useCallback(async () => {
    try {
      const params = new URLSearchParams({ minutes: String(minutes) });
      if (selectedDept) params.append("department", selectedDept);
      if (assetUuid) params.append("asset_uuid", assetUuid);
      const res = await axios.get(`${API}/clients/${clientId}/iot/pmc53a/timeseries?${params}`);
      setTimeseries(res.data);
      setLastRefresh(new Date());
    } catch (e) {
      console.error("Timeseries fetch error:", e);
    }
  }, [clientId, selectedDept, minutes, assetUuid]);

  useEffect(() => {
    fetchTimeseries();
  }, [fetchTimeseries]);

  useEffect(() => {
    if (!polling) return;
    const interval = setInterval(fetchTimeseries, 15000);
    return () => clearInterval(interval);
  }, [polling, fetchTimeseries]);

  const series = timeseries?.series || [];
  const snapshot = timeseries?.snapshot || {};
  const activeDepts = timeseries?.departments_active || [];
  const deptKeys = ["building_facilities","operations_equipment","transportation_logistics","procurement_waste","administrative","it_infrastructure"];

  const currentDept = selectedDept || activeDepts[0] || "";
  const currentSnap = snapshot[currentDept] || {};

  const metricGroups = [
    { title: t("voltage_phase"), keys: ["voltage_l1","voltage_l2","voltage_l3"], color: "#3B82F6", unit: "V" },
    { title: t("current_phase"), keys: ["current_l1","current_l2","current_l3"], color: "#C86B4F", unit: "A" },
    { title: t("power_metrics"), keys: ["active_power","reactive_power","apparent_power"], color: "#244C36", unit: "kW/kVAr/kVA" },
    { title: t("quality_metrics"), keys: ["power_factor","frequency"], color: "#D69E2E", unit: "PF/Hz" },
  ];

  return (
    <div data-testid="live-monitor-panel">
      <div className="live-controls" data-testid="live-controls">
        <div className="live-controls-left">
          <select className="form-input" value={selectedDept} onChange={e => setSelectedDept(e.target.value)} data-testid="live-dept-select" style={{maxWidth:250}}>
            <option value="">{t("all_departments")}</option>
            {deptKeys.map(dk => <option key={dk} value={dk}>{dn(dk)}</option>)}
          </select>
          <select className="form-input" value={minutes} onChange={e => setMinutes(Number(e.target.value))} data-testid="live-range-select" style={{maxWidth:150}}>
            <option value={15}>15 min</option>
            <option value={30}>30 min</option>
            <option value={60}>1 hr</option>
            <option value={180}>3 hr</option>
            <option value={720}>12 hr</option>
          </select>
        </div>
        <div className="live-controls-right">
          <div className={`live-indicator ${polling ? "active" : ""}`} data-testid="live-indicator">
            <span className="live-dot"></span>
            {polling ? "LIVE" : "PAUSED"}
          </div>
          <button className={`btn ${polling ? "btn-secondary" : "btn-primary"}`} onClick={() => setPolling(!polling)} data-testid="live-toggle">
            {polling ? t("pause") : t("resume")}
          </button>
          <button className="btn btn-primary" onClick={fetchTimeseries} data-testid="live-refresh">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {lastRefresh && (
        <p className="text-muted mb-md" style={{ fontSize: "var(--text-small)" }}>
          {t("last_update")}: {lastRefresh.toLocaleTimeString()} — {timeseries?.data_points || 0} {t("data_points")}
        </p>
      )}

      {currentDept && Object.keys(currentSnap).length > 0 && (
        <div className="live-snapshot mb-lg" data-testid="live-snapshot">
          <h4 className="card-title" style={{marginBottom:"var(--space-md)"}}>
            <Monitor size={18}/> {dn(currentDept)} — {t("current_values")}
          </h4>
          <div className="live-values-grid">
            {[
              {key:"voltage_l1",label:"V L1",icon:"V"},
              {key:"voltage_l2",label:"V L2",icon:"V"},
              {key:"voltage_l3",label:"V L3",icon:"V"},
              {key:"current_l1",label:"I L1",icon:"A"},
              {key:"current_l2",label:"I L2",icon:"A"},
              {key:"current_l3",label:"I L3",icon:"A"},
              {key:"active_power",label:"P (kW)",icon:"kW"},
              {key:"reactive_power",label:"Q (kVAr)",icon:"kVAr"},
              {key:"apparent_power",label:"S (kVA)",icon:"kVA"},
              {key:"power_factor",label:"PF",icon:""},
              {key:"frequency",label:"Freq",icon:"Hz"},
              {key:"energy_total",label:"Energy",icon:"kWh"},
            ].filter(m => currentSnap[m.key]).map(m => (
              <div key={m.key} className="live-value-card" data-testid={`live-val-${m.key}`}>
                <div className="live-value-label">{m.label}</div>
                <div className="live-value-num">
                  {currentSnap[m.key].value?.toLocaleString(undefined, {maximumFractionDigits: m.key === "power_factor" ? 3 : 1})}
                </div>
                <div className="live-value-unit">{currentSnap[m.key].unit || m.icon}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {series.length > 0 ? (
        <div className="live-charts" data-testid="live-charts">
          {metricGroups.map((group, gi) => {
            const hasData = group.keys.some(k => series.some(s => s[k] !== undefined));
            if (!hasData) return null;
            return (
              <div key={gi} className="card mb-lg">
                <h4 className="card-title">{group.title}</h4>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2EAE5" />
                    <XAxis dataKey="time" tick={{ fill: "#526A5A", fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fill: "#526A5A", fontSize: 10 }} domain={["auto", "auto"]} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2EAE5", borderRadius: 8, fontSize: 12 }} />
                    <Legend />
                    {group.keys.map((k, ki) => (
                      <Line key={k} type="monotone" dataKey={k} stroke={LIVE_CHART_COLORS[ki % LIVE_CHART_COLORS.length]}
                        strokeWidth={2} dot={false} name={k.replace(/_/g," ")} connectNulls />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            );
          })}

          {series.some(s => s.energy_total !== undefined) && (
            <div className="card mb-lg">
              <h4 className="card-title">{t("energy_trend")}</h4>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2EAE5" />
                  <XAxis dataKey="time" tick={{ fill: "#526A5A", fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fill: "#526A5A", fontSize: 10 }} domain={["auto", "auto"]} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2EAE5", borderRadius: 8, fontSize: 12 }} />
                  <Line type="monotone" dataKey="energy_total" stroke="#244C36" strokeWidth={2.5} dot={false} name="Energy Total (kWh)" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {series.some(s => s.thd_voltage_l1 !== undefined || s.thd_current_l1 !== undefined) && (
            <div className="card mb-lg">
              <h4 className="card-title">THD ({t("harmonic_distortion")})</h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2EAE5" />
                  <XAxis dataKey="time" tick={{ fill: "#526A5A", fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fill: "#526A5A", fontSize: 10 }} unit="%" />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2EAE5", borderRadius: 8, fontSize: 12 }} />
                  <Legend />
                  <Line type="monotone" dataKey="thd_voltage_l1" stroke="#3B82F6" strokeWidth={2} dot={false} name="THD Voltage L1" connectNulls />
                  <Line type="monotone" dataKey="thd_current_l1" stroke="#C86B4F" strokeWidth={2} dot={false} name="THD Current L1" connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      ) : (
        <div className="card" style={{textAlign:"center", padding:"var(--space-xxl)"}}>
          <Radio size={48} strokeWidth={1} style={{color:"var(--text-muted)", marginBottom:"var(--space-md)"}} />
          <p className="text-muted">{t("no_timeseries")}</p>
          <p className="text-muted" style={{fontSize:"var(--text-small)"}}>{t("simulate_or_push")}</p>
        </div>
      )}
    </div>
  );
};

export default LiveMonitorPanel;
